import { useRef, useEffect, useCallback, useState } from 'react';
import * as fabric from 'fabric';
import type { EditorTool, BrushSettings, ShapeSettings, FilterSettings, ShapeType } from '../types';

interface UseFabricCanvasOptions {
  canvasElRef: React.RefObject<HTMLCanvasElement | null>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  onObjectModified?: () => void;
  onSelectionChanged?: (objectIds: string[]) => void;
  onCanvasDimensions?: (width: number, height: number) => void;
  onCursorMove?: (x: number, y: number, color: string) => void;
  onPathCreated?: () => void;
  onZoomChanged?: (zoom: number) => void;
}

/** Viewport info for scrollbar indicators */
export interface ViewportInfo {
  thumbWidthRatio: number;
  thumbHeightRatio: number;
  scrollXRatio: number;
  scrollYRatio: number;
  showH: boolean;
  showV: boolean;
}

export function useFabricCanvas({
  canvasElRef,
  wrapperRef,
  onObjectModified,
  onSelectionChanged,
  onCanvasDimensions,
  onCursorMove,
  onPathCreated,
  onZoomChanged,
}: UseFabricCanvasOptions) {
  const canvasRef = useRef<fabric.Canvas | null>(null);
  const backgroundImageRef = useRef<fabric.FabricImage | null>(null);
  const imageDimsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  const activeToolRef = useRef<EditorTool>('select');
  const marqueeRef = useRef<{
    rect: fabric.Rect | null;
    startX: number;
    startY: number;
    isDrawing: boolean;
  }>({ rect: null, startX: 0, startY: 0, isDrawing: false });
  const onZoomChangedRef = useRef(onZoomChanged);
  onZoomChangedRef.current = onZoomChanged;
  const updateViewportInfoRef = useRef<() => void>(() => {});

  const [viewport, setViewport] = useState<ViewportInfo>({
    thumbWidthRatio: 1,
    thumbHeightRatio: 1,
    scrollXRatio: 0,
    scrollYRatio: 0,
    showH: false,
    showV: false,
  });

  /** Compute scrollbar indicator state from current viewport transform */
  const updateViewportInfo = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const vt = canvas.viewportTransform;
    if (!vt) return;

    const zoom = vt[0];
    const panX = vt[4];
    const panY = vt[5];
    const vpW = wrapper.clientWidth;
    const vpH = wrapper.clientHeight;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;

    if (!imgW || !imgH) {
      setViewport({
        thumbWidthRatio: 1,
        thumbHeightRatio: 1,
        scrollXRatio: 0,
        scrollYRatio: 0,
        showH: false,
        showV: false,
      });
      return;
    }

    const contentW = imgW * zoom;
    const contentH = imgH * zoom;

    const thumbWR = Math.min(1, vpW / contentW);
    const thumbHR = Math.min(1, vpH / contentH);

    const maxPanX = 0;
    const minPanX = vpW - contentW;
    const scrollXR = minPanX < maxPanX ? (maxPanX - panX) / (maxPanX - minPanX) : 0;

    const maxPanY = 0;
    const minPanY = vpH - contentH;
    const scrollYR = minPanY < maxPanY ? (maxPanY - panY) / (maxPanY - minPanY) : 0;

    setViewport({
      thumbWidthRatio: thumbWR,
      thumbHeightRatio: thumbHR,
      scrollXRatio: Math.max(0, Math.min(1, scrollXR)),
      scrollYRatio: Math.max(0, Math.min(1, scrollYR)),
      showH: thumbWR < 0.99,
      showV: thumbHR < 0.99,
    });
  }, [wrapperRef]);

  // Keep ref in sync so event handlers in useEffect can call the latest version
  updateViewportInfoRef.current = updateViewportInfo;

  // Initialize canvas
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    const wrapper = wrapperRef.current;
    if (!canvasEl || !wrapper) return;

    const canvas = new fabric.Canvas(canvasEl, {
      backgroundColor: 'transparent',
      selection: true,
      preserveObjectStacking: true,
    });

    canvasRef.current = canvas;

    // Force Fabric's auto-created wrapper to fill our container
    if (canvas.wrapperEl) {
      canvas.wrapperEl.style.position = 'absolute';
      canvas.wrapperEl.style.inset = '0';
      canvas.wrapperEl.style.width = '100%';
      canvas.wrapperEl.style.height = '100%';
    }

    // Canvas element always matches wrapper size
    const resize = () => {
      const w = wrapper.clientWidth;
      const h = wrapper.clientHeight;
      if (w > 0 && h > 0) {
        canvas.setDimensions({ width: w, height: h });
        canvas.renderAll();
      }
    };
    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(wrapper);

    // Event listeners
    canvas.on('object:modified', () => onObjectModified?.());
    canvas.on('path:created', () => onPathCreated?.());

    canvas.on('selection:created', (e) => {
      const ids =
        e.selected?.map((o) => (o as fabric.FabricObject & { id?: string }).id || '') || [];
      onSelectionChanged?.(ids);
    });
    canvas.on('selection:updated', (e) => {
      const ids =
        e.selected?.map((o) => (o as fabric.FabricObject & { id?: string }).id || '') || [];
      onSelectionChanged?.(ids);
    });
    canvas.on('selection:cleared', () => onSelectionChanged?.([]));

    canvas.on('mouse:move', (opt) => {
      if (!opt.e) return;
      const pointer = canvas.getScenePoint(opt.e);
      const ctx = canvas.getContext();
      const pixel = ctx.getImageData(
        Math.round(pointer.x * (canvas.getRetinaScaling?.() || 1)),
        Math.round(pointer.y * (canvas.getRetinaScaling?.() || 1)),
        1,
        1,
      ).data;
      const hex = `#${pixel[0].toString(16).padStart(2, '0')}${pixel[1].toString(16).padStart(2, '0')}${pixel[2].toString(16).padStart(2, '0')}`;
      onCursorMove?.(Math.round(pointer.x), Math.round(pointer.y), hex);
    });

    // Mouse wheel zoom — zoom toward the cursor position
    canvas.on('mouse:wheel', (opt) => {
      const e = opt.e as WheelEvent;
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY;
      const vt = canvas.viewportTransform;
      if (!vt) return;

      const currentZoom = vt[0];
      const factor = delta > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.1, Math.min(10, currentZoom * factor));

      // Zoom toward the mouse position
      const point = canvas.getScenePoint(e);
      canvas.zoomToPoint(point, newZoom);
      canvas.renderAll();

      onZoomChangedRef.current?.(newZoom);
      updateViewportInfoRef.current();
    });

    // Marquee selection — draw a dashed rectangle on drag when select tool is active
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'select') return;
      if (opt.e === undefined) return;

      // Don't start marquee if clicking on an existing object (let Fabric handle that)
      if (canvas.findTarget(opt.e)) return;

      const pointer = canvas.getScenePoint(opt.e);
      const m = marqueeRef.current;

      // Remove previous marquee rect if any
      if (m.rect) {
        canvas.remove(m.rect);
        m.rect = null;
      }

      const rect = new fabric.Rect({
        left: pointer.x,
        top: pointer.y,
        width: 0,
        height: 0,
        fill: 'rgba(0, 120, 215, 0.1)',
        stroke: '#0078d7',
        strokeWidth: 1,
        strokeDashArray: [5, 3],
        selectable: false,
        evented: false,
        excludeFromExport: true,
      });
      (rect as fabric.Rect & { _isMarquee: boolean })._isMarquee = true;

      canvas.add(rect);
      canvas.renderAll();

      marqueeRef.current = {
        rect,
        startX: pointer.x,
        startY: pointer.y,
        isDrawing: true,
      };
    });

    canvas.on('mouse:move', (opt) => {
      const m = marqueeRef.current;
      if (!m.isDrawing || !m.rect || !opt.e) return;

      const pointer = canvas.getScenePoint(opt.e);
      const left = Math.min(m.startX, pointer.x);
      const top = Math.min(m.startY, pointer.y);
      const width = Math.abs(pointer.x - m.startX);
      const height = Math.abs(pointer.y - m.startY);

      m.rect.set({ left, top, width, height });
      canvas.renderAll();
    });

    canvas.on('mouse:up', () => {
      const m = marqueeRef.current;
      if (!m.isDrawing || !m.rect) return;
      m.isDrawing = false;

      // Remove if too small (accidental click)
      if ((m.rect.width || 0) < 3 && (m.rect.height || 0) < 3) {
        canvas.remove(m.rect);
        m.rect = null;
      }

      canvas.renderAll();
    });

    return () => {
      observer.disconnect();
      canvas.dispose();
      canvasRef.current = null;
    };
  }, []);

  /** Zoom and pan the viewport so the image fits centered in the wrapper */
  const zoomToFit = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return 1;

    const vpW = wrapper.clientWidth;
    const vpH = wrapper.clientHeight;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (!imgW || !imgH || !vpW || !vpH) return 1;

    // Ensure canvas element matches wrapper
    canvas.setDimensions({ width: vpW, height: vpH });

    const padding = 40;
    const zoom = Math.min((vpW - padding * 2) / imgW, (vpH - padding * 2) / imgH, 1);

    const panX = (vpW - imgW * zoom) / 2;
    const panY = (vpH - imgH * zoom) / 2;

    canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
    canvas.renderAll();
    updateViewportInfo();
    return zoom;
  }, [wrapperRef, updateViewportInfo]);

  /** Load an image onto the canvas. Returns the fit zoom level. */
  const loadImage = useCallback(
    async (src: string): Promise<number> => {
      const canvas = canvasRef.current;
      if (!canvas) return 1;

      const img = await fabric.FabricImage.fromURL(src, { crossOrigin: 'anonymous' });
      backgroundImageRef.current = img;

      const imgW = img.width || 1;
      const imgH = img.height || 1;
      imageDimsRef.current = { width: imgW, height: imgH };

      img.set({
        left: 0,
        top: 0,
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
        erasable: false,
      } as Partial<fabric.FabricImage>);

      canvas.clear();
      canvas.add(img);
      canvas.sendObjectToBack(img);

      // Wait for layout to settle, then fit
      await new Promise((r) => requestAnimationFrame(r));
      const zoom = zoomToFit();

      onCanvasDimensions?.(imgW, imgH);
      return zoom;
    },
    [onCanvasDimensions, zoomToFit],
  );

  /** Enable free drawing mode */
  const enableDrawMode = useCallback((settings: BrushSettings) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = true;
    const brush = new fabric.PencilBrush(canvas);
    brush.color = settings.color;
    brush.width = settings.width;
    canvas.freeDrawingBrush = brush;
  }, []);

  /** Disable drawing mode */
  const disableDrawMode = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = false;
  }, []);

  /** Add a text object to the canvas center (in image coordinates) */
  const addText = useCallback(
    (
      text: string,
      options: {
        left?: number;
        top?: number;
        fontSize?: number;
        fill?: string;
        fontFamily?: string;
      },
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;

      const textObj = new fabric.IText(text, {
        left: options.left ?? imgW / 2,
        top: options.top ?? imgH / 2,
        fontSize: options.fontSize ?? 24,
        fill: options.fill ?? '#000000',
        fontFamily: options.fontFamily ?? 'Arial',
        originX: 'center',
        originY: 'center',
      });

      (textObj as fabric.IText & { id: string }).id = `text_${Date.now()}`;
      canvas.add(textObj);
      canvas.setActiveObject(textObj);
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Add a shape to the canvas center (in image coordinates) */
  const addShape = useCallback(
    (shapeType: ShapeType, settings: ShapeSettings) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      const center = { left: imgW / 2, top: imgH / 2 };
      let shape: fabric.FabricObject;

      switch (shapeType) {
        case 'rect':
          shape = new fabric.Rect({
            ...center,
            width: 150,
            height: 100,
            fill: settings.fill,
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            rx: settings.cornerRadius,
            ry: settings.cornerRadius,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'circle':
          shape = new fabric.Circle({
            ...center,
            radius: 60,
            fill: settings.fill,
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'triangle':
          shape = new fabric.Triangle({
            ...center,
            width: 120,
            height: 120,
            fill: settings.fill,
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'line':
          shape = new fabric.Line([center.left - 75, center.top, center.left + 75, center.top], {
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
          });
          break;
        default:
          return;
      }

      (shape as fabric.FabricObject & { id: string }).id = `shape_${Date.now()}`;
      canvas.add(shape);
      canvas.setActiveObject(shape);
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Apply filters to the background image */
  const applyFilters = useCallback((settings: FilterSettings) => {
    const img = backgroundImageRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;

    img.filters = [];

    if (settings.brightness !== 0) {
      img.filters.push(new fabric.filters.Brightness({ brightness: settings.brightness / 100 }));
    }
    if (settings.contrast !== 0) {
      img.filters.push(new fabric.filters.Contrast({ contrast: settings.contrast / 100 }));
    }
    if (settings.saturation !== 0) {
      img.filters.push(new fabric.filters.Saturation({ saturation: settings.saturation / 100 }));
    }

    img.applyFilters();
    canvas.renderAll();
  }, []);

  /** Delete pixels within marquee selection, or remove selected objects */
  const deleteSelected = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const m = marqueeRef.current;

    // If there's a marquee selection, erase pixels in that region
    if (m.rect && !m.isDrawing) {
      const img = backgroundImageRef.current;
      if (img) {
        const rect = m.rect;
        const left = Math.round(rect.left ?? 0);
        const top = Math.round(rect.top ?? 0);
        const width = Math.round(rect.width ?? 0);
        const height = Math.round(rect.height ?? 0);

        if (width > 0 && height > 0) {
          // Get the image element from Fabric
          const imgEl = img.getElement() as HTMLImageElement | HTMLCanvasElement;

          // Draw image to an offscreen canvas so we can modify pixels
          const offscreen = document.createElement('canvas');
          offscreen.width = img.width || 0;
          offscreen.height = img.height || 0;
          const ctx = offscreen.getContext('2d');
          if (ctx) {
            ctx.drawImage(imgEl, 0, 0);
            // Clear the marquee region (make transparent)
            ctx.clearRect(left, top, width, height);

            // Replace the background image with the modified one
            const newImg = new fabric.FabricImage(offscreen, {
              left: 0,
              top: 0,
              scaleX: 1,
              scaleY: 1,
              selectable: false,
              evented: false,
            });
            (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;

            // Preserve any existing filters
            newImg.filters = img.filters ? [...img.filters] : [];
            newImg.applyFilters();

            canvas.remove(img);
            canvas.add(newImg);
            canvas.sendObjectToBack(newImg);
            backgroundImageRef.current = newImg;
          }
        }
      }

      // Remove the marquee rect
      canvas.remove(m.rect);
      m.rect = null;
      canvas.renderAll();
      onObjectModified?.();
      return;
    }

    // Otherwise, delete selected Fabric objects
    const active = canvas.getActiveObjects();
    if (active.length === 0) return;
    active.forEach((obj) => {
      if (obj === backgroundImageRef.current) return;
      canvas.remove(obj);
    });
    canvas.discardActiveObject();
    canvas.renderAll();
    onObjectModified?.();
  }, [onObjectModified]);

  /** Set canvas zoom level, keeping content centered */
  const setZoom = useCallback(
    (zoom: number) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas || !wrapper) return;

      const vpW = wrapper.clientWidth;
      const vpH = wrapper.clientHeight;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;

      const panX = (vpW - imgW * zoom) / 2;
      const panY = (vpH - imgH * zoom) / 2;
      canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
      canvas.renderAll();
      updateViewportInfo();
    },
    [wrapperRef, updateViewportInfo],
  );

  /** Fit canvas to container (re-center and scale to fit) */
  const fitToContainer = useCallback(() => {
    return zoomToFit();
  }, [zoomToFit]);

  /** Rotate the entire canvas by degrees */
  const rotateCanvas = useCallback(
    (degrees: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.getObjects().forEach((obj) => {
        const currentAngle = obj.angle || 0;
        obj.rotate(currentAngle + degrees);
      });
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Flip canvas objects */
  const flipCanvas = useCallback(
    (direction: 'horizontal' | 'vertical') => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.getObjects().forEach((obj) => {
        if (direction === 'horizontal') {
          obj.set('flipX', !obj.flipX);
        } else {
          obj.set('flipY', !obj.flipY);
        }
      });
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Export canvas to data URL */
  const exportToDataURL = useCallback((format: 'png' | 'jpeg' | 'webp' = 'png', quality = 1) => {
    const canvas = canvasRef.current;
    if (!canvas) return '';
    return canvas.toDataURL({ format, quality, multiplier: 1 });
  }, []);

  /** Export canvas to blob */
  const exportToBlob = useCallback(
    (format: 'png' | 'jpeg' | 'webp' = 'png', quality = 1): Promise<Blob | null> => {
      const canvas = canvasRef.current;
      if (!canvas) return Promise.resolve(null);
      return new Promise((resolve) => {
        const dataUrl = canvas.toDataURL({ format, quality, multiplier: 1 });
        fetch(dataUrl)
          .then((res) => res.blob())
          .then(resolve)
          .catch(() => resolve(null));
      });
    },
    [],
  );

  /** Serialize canvas to JSON */
  const toJSON = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.toJSON();
  }, []);

  /** Load canvas from JSON */
  const loadFromJSON = useCallback(async (json: object) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    await canvas.loadFromJSON(json);
    canvas.renderAll();
    const objects = canvas.getObjects();
    const bgImg = objects.find(
      (o) =>
        o instanceof fabric.FabricImage &&
        !(o as fabric.FabricImage & { selectable: boolean }).selectable,
    );
    if (bgImg) {
      backgroundImageRef.current = bgImg as fabric.FabricImage;
    }
  }, []);

  /** Clear the marquee selection rectangle if present */
  const clearMarquee = useCallback(() => {
    const canvas = canvasRef.current;
    const m = marqueeRef.current;
    if (canvas && m.rect) {
      canvas.remove(m.rect);
      m.rect = null;
      m.isDrawing = false;
      canvas.renderAll();
    }
  }, []);

  /** Set the active tool on the canvas */
  const setTool = useCallback(
    (tool: EditorTool, brushSettings?: BrushSettings) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      activeToolRef.current = tool;

      // Clear marquee when switching away from select
      if (tool !== 'select') {
        clearMarquee();
      }

      canvas.isDrawingMode = false;
      canvas.selection = false; // We handle selection via marquee
      canvas.defaultCursor = 'default';

      switch (tool) {
        case 'select':
          canvas.defaultCursor = 'crosshair';
          canvas.getObjects().forEach((o) => {
            if (o !== backgroundImageRef.current) {
              o.set({ selectable: false, evented: false });
            }
          });
          break;
        case 'draw':
          if (brushSettings) enableDrawMode(brushSettings);
          break;
        case 'eraser':
          canvas.isDrawingMode = true;
          canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
          canvas.freeDrawingBrush.color = '#ffffff';
          canvas.freeDrawingBrush.width = 20;
          break;
        case 'crop':
          canvas.defaultCursor = 'crosshair';
          break;
        case 'text':
          canvas.defaultCursor = 'text';
          break;
        case 'shape':
          canvas.defaultCursor = 'crosshair';
          break;
        case 'eyedropper':
          canvas.defaultCursor = 'crosshair';
          break;
        case 'fill':
          canvas.defaultCursor = 'crosshair';
          break;
      }

      canvas.renderAll();
    },
    [enableDrawMode],
  );

  /** Apply crop to the canvas */
  const applyCrop = useCallback(
    (rect: { left: number; top: number; width: number; height: number }) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const clipRect = new fabric.Rect({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        absolutePositioned: true,
      });

      canvas.getObjects().forEach((obj) => {
        obj.clipPath = clipRect;
      });

      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Select all objects (excluding background) */
  const selectAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const objects = canvas.getObjects().filter((o) => o !== backgroundImageRef.current);
    if (objects.length > 0) {
      const selection = new fabric.ActiveSelection(objects, { canvas });
      canvas.setActiveObject(selection);
      canvas.renderAll();
    }
  }, []);

  return {
    canvasRef,
    viewport,
    loadImage,
    enableDrawMode,
    disableDrawMode,
    addText,
    addShape,
    applyFilters,
    deleteSelected,
    setZoom,
    fitToContainer,
    rotateCanvas,
    flipCanvas,
    exportToDataURL,
    exportToBlob,
    toJSON,
    loadFromJSON,
    setTool,
    applyCrop,
    selectAll,
    clearMarquee,
  };
}
